<script setup lang="tsx">
import { ref, watch } from 'vue'
import Multiselect from 'vue-multiselect'
import type { Response, StandardMethod } from '@/models/api-generated'
import http from '@/services/http'
import type { MeasureFilter } from '@/models/filters-generated'
import { useI18n } from 'vue-i18n'
import ClearButton from '../filter/ClearButton.vue'

const props = defineProps<{ moduleId?: number; validInput: boolean }>()
const model = defineModel<StandardMethod[]>()
const isOpen = ref(false)

const { t } = useI18n()

const isLoading = ref(false)
const options = ref<StandardMethod[]>([])

const search = async (q: string = '') => {
	if (!props.moduleId) return

	isLoading.value = true

	const flt: MeasureFilter = {
		q,
		moduleId: props.moduleId,
	}

	const { data } = await http.get<Response<StandardMethod[]>>('api/risk/measures', {
		params: flt,
	})

	options.value = data.data ?? []
	isLoading.value = false
}

watch(
	() => props.moduleId,
	async (moduleId) => {
		if (moduleId) await search()
	},
	{ immediate: true },
)
</script>

<template>
	<Multiselect
		:class="{ 'is-invalid': !props.validInput }"
		:multiple="true"
		:disabled="!props.moduleId"
		:show-labels="false"
		:options="options"
		:internal-search="false"
		:preserve-search="true"
		:show-no-results="false"
		:loading="isLoading"
		v-model="model"
		label="name"
		track-by="id"
		@search-change="search"
		:placeholder="t('method.search') + '...'"
		:taggable="false"
		:hide-selected="true"
		:close-on-select="false"
		:clear-on-select="false"
		:id="props.moduleId"
		@open="isOpen = true"
		@close="isOpen = false"
		:limit="isOpen ? 100 : 3"
	>
		<template #option="{ option }">
			<div class="d-flex justify-content-between align-items-center w-100">
				<span v-one-tooltip="option.description">{{ option.name }}</span>
			</div>
		</template>

		<template #clear>
			<ClearButton right="2rem" v-if="model && model.length" @click="model = []"></ClearButton>
		</template>

		<template v-if="!isOpen" #limit>
			<strong v-if="model" class="multiselect__strong">{{ $t('misc.limit', [model.length - 3]) }}</strong>
		</template>
	</Multiselect>
</template>
